import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  HiX, 
  HiOutlinePaperAirplane,
  HiOutlineSparkles 
} from 'react-icons/hi';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const GROQ_API_KEY = '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';

// Frontend AI: Generate UI dynamically based on data
const generateUIFromData = async (result, userMessage) => {
  if (!result) return null;

  try {
    const response = await fetch('https://api.groq.com/openai/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${GROQ_API_KEY}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        model: 'llama-3.3-70b-versatile',
        messages: [
          {
            role: 'system',
            content: `You are a UI generator for LUXE - a luxury ecommerce admin chatbot.

🎨 LUXE BRAND DESIGN SYSTEM:
- Primary Color: #c9a962 (luxury-gold) - use for prices, highlights, CTAs
- Background: White (#ffffff) with subtle gray (#f9fafb, #f3f4f6)
- Text: Dark gray (#1f2937, #374151, #6b7280)
- Accent: Charcoal (#1a1a1a) for headers
- Shadows: Use shadow-sm, shadow-md for depth
- Rounded: rounded-lg for cards, rounded for images
- Spacing: p-2, p-3 for compact chat display

📐 RESPONSIVE CONSTRAINTS:
- You are rendering inside a 384px wide chat window
- Maximum width: w-full (don't use fixed widths like w-96)
- For grids: use grid-cols-1 or grid-cols-2 max (never more)
- For images: max-h-24 or max-h-32 (keep them small)
- Text: text-xs, text-sm (never large text)
- Keep everything compact and scrollable

🎯 YOUR TASK:
Generate beautiful, responsive HTML with Tailwind CSS to display data elegantly in a chat context.

📋 DISPLAY RULES:
- Products: Show in a grid with images, name in text-gray-800, price in text-yellow-600 font-bold
- Orders: Show as cards with order number prominent, customer name, items with small thumbnails
- Analytics: Large numbers in text-green-600 or text-yellow-600, use bg-gradient-to-r
- Lists: Use space-y-2 with small cards
- Images: Always use rounded corners and object-cover
- Hover effects: hover:shadow-lg transition

🚫 AVOID:
- Large text sizes (text-lg, text-xl, text-2xl)
- Wide elements (w-96, w-screen)
- More than 2 columns
- Buttons (this is read-only display)
- Fixed heights that break scrolling

✅ GOOD EXAMPLE (Products):
<div class="space-y-2">
  <div class="bg-white p-2 rounded-lg shadow-sm border border-gray-100 flex gap-2">
    <img src="..." class="w-16 h-16 object-cover rounded"/>
    <div class="flex-1 min-w-0">
      <p class="font-semibold text-gray-800 text-sm truncate">Product Name</p>
      <p class="text-yellow-600 font-bold text-xs">250 TND</p>
      <p class="text-gray-500 text-xs">Stock: 10</p>
    </div>
  </div>
</div>

✅ GOOD EXAMPLE (Orders):
<div class="space-y-2">
  <div class="bg-gradient-to-r from-gray-50 to-gray-100 p-3 rounded-lg shadow-sm">
    <p class="font-bold text-yellow-600 text-sm">Order #12345</p>
    <p class="text-gray-700 text-xs">👤 John Doe</p>
    <p class="text-gray-600 text-xs">📞 +216 12345678</p>
    <div class="mt-2 space-y-1">
      <div class="flex gap-2 bg-white p-1 rounded">
        <img src="..." class="w-12 h-12 object-cover rounded"/>
        <div class="text-xs">
          <p class="font-semibold text-gray-800">Item Name</p>
          <p class="text-gray-600">2 × 100 TND</p>
        </div>
      </div>
    </div>
  </div>
</div>

🎯 RESPOND WITH ONLY HTML (no markdown, no code blocks, just raw HTML)

Remember: Compact, elegant, luxury aesthetic with gold accents!`
          },
          {
            role: 'user',
            content: `User asked: "${userMessage}"\n\nData to display:\n${JSON.stringify(result, null, 2)}\n\nGenerate beautiful, compact HTML to display this data.`
          }
        ],
        temperature: 0.5,
        max_tokens: 2000
      })
    });

    const data = await response.json();
    const html = data.choices[0]?.message?.content || '';
    
    // Clean up HTML (remove code blocks if AI added them)
    return html.replace(/```html\n?/g, '').replace(/```\n?/g, '').trim();
  } catch (error) {
    console.error('Frontend AI error:', error);
    return null;
  }
};

const AdminAIChatbot = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hello! I\'m your AI admin assistant. I can help you manage products, orders, inventory, and more. Just ask me anything!',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId] = useState(Date.now());
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Only show to admin users
  if (!user || user.role !== 'admin') {
    return null;
  }

  const sendMessage = async (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const savedMessage = inputMessage;
    setInputMessage('');
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/ai/chat`,
        {
          message: savedMessage,
          conversationHistory: messages,
          conversationId
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Generate UI dynamically using frontend AI
      let generatedUI = null;
      if (response.data.result && response.data.executed) {
        console.log('🎨 Generating UI with frontend AI...');
        generatedUI = await generateUIFromData(response.data.result, savedMessage);
      }

      const aiMessage = {
        role: 'assistant',
        content: response.data.message,
        thought: response.data.thought,
        code: response.data.code,
        result: response.data.result,
        generatedUI, // AI-generated HTML
        executed: response.data.executed,
        error: response.data.error,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('AI Chat error:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        isError: true,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatResult = (result) => {
    if (!result) return null;

    // Handle arrays (list of items)
    if (Array.isArray(result)) {
      // Check if it's a list of products
      if (result[0]?.name && (result[0]?.price !== undefined || result[0]?.slug)) {
        return (
          <div className="mt-2 space-y-2">
            <p className="font-semibold text-luxury-gold">Found {result.length} products:</p>
            <div className="grid grid-cols-1 gap-2 max-h-96 overflow-auto">
              {result.map((product, idx) => (
                <div key={idx} className="flex items-center gap-3 bg-white p-3 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition">
                  {product.images?.[0]?.url && (
                    <img 
                      src={product.images[0].url} 
                      alt={product.name} 
                      className="w-20 h-20 object-cover rounded"
                    />
                  )}
                  <div className="flex-1">
                    <p className="font-bold text-gray-800">{product.name}</p>
                    {product.price !== undefined && (
                      <p className="text-luxury-gold font-semibold">{product.price} TND</p>
                    )}
                    {product.stock !== undefined && (
                      <p className="text-sm text-gray-600">Stock: {product.stock}</p>
                    )}
                    {product.category?.name && (
                      <p className="text-xs text-gray-500">📁 {product.category.name}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      }

      // Check if it's a list of orders
      if (result[0]?.orderNumber || result[0]?.items) {
        return (
          <div className="mt-2 space-y-3">
            {result.map((order, idx) => (
              <div key={idx} className="p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg shadow-sm border border-gray-200">
                {order.orderNumber && (
                  <p className="font-bold text-luxury-gold text-lg mb-2">Order #{order.orderNumber}</p>
                )}
                {order.client?.name && (
                  <p className="text-gray-800">👤 <span className="font-semibold">Made by:</span> {order.client.name}</p>
                )}
                {order.customer?.name && (
                  <p className="text-gray-800">👤 <span className="font-semibold">Customer:</span> {order.customer.name}</p>
                )}
                {order.customer?.phone && (
                  <p className="text-gray-700">📞 {order.customer.phone}</p>
                )}
                {order.customer?.email && (
                  <p className="text-gray-700">📧 {order.customer.email}</p>
                )}
                {order.status && (
                  <p className="text-gray-700">📦 <span className="font-semibold">Status:</span> <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">{order.status}</span></p>
                )}
                {order.total && (
                  <p className="text-gray-800 font-semibold mt-1">💰 Total: {order.total}</p>
                )}
                {order.items && order.items.length > 0 && (
                  <div className="mt-2 border-t pt-2">
                    <p className="font-semibold text-gray-700 mb-2">📦 Items:</p>
                    <div className="space-y-2">
                      {order.items.map((item, itemIdx) => (
                        <div key={itemIdx} className="flex items-center gap-3 bg-white p-2 rounded">
                          {item.image && (
                            <img src={item.image} alt={item.product} className="w-16 h-16 object-cover rounded" />
                          )}
                          <div className="flex-1">
                            <p className="font-semibold text-gray-800">{item.product}</p>
                            <p className="text-sm text-gray-600">Qty: {item.quantity} × {item.price} TND</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      }

      // Generic array display
      return (
        <div className="mt-2 p-3 bg-gray-50 rounded text-sm">
          <p className="font-semibold text-luxury-gold mb-2">Found {result.length} items:</p>
          <div className="space-y-2 max-h-60 overflow-auto">
            {result.slice(0, 10).map((item, idx) => (
              <div key={idx} className="p-2 bg-white rounded border border-gray-200">
                {typeof item === 'object' ? (
                  <div className="text-xs">
                    {Object.entries(item).slice(0, 4).map(([key, value]) => (
                      <p key={key} className="text-gray-700">
                        <span className="font-semibold">{key}:</span> {String(value)}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-700">{String(item)}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Handle single object
    if (typeof result === 'object') {
      // Single order display
      if (result.orderNumber || result.items) {
        return (
          <div className="mt-2 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg shadow-md border border-gray-200">
            {result.orderNumber && (
              <p className="font-bold text-luxury-gold text-lg mb-2">Order #{result.orderNumber}</p>
            )}
            {result.client?.name && (
              <p className="text-gray-800">👤 <span className="font-semibold">Made by:</span> {result.client.name}</p>
            )}
            {result.customer?.name && (
              <p className="text-gray-800">👤 <span className="font-semibold">Customer:</span> {result.customer.name}</p>
            )}
            {result.customer?.phone && (
              <p className="text-gray-700">📞 {result.customer.phone}</p>
            )}
            {result.customer?.email && (
              <p className="text-gray-700">📧 {result.customer.email}</p>
            )}
            {result.status && (
              <p className="text-gray-700 mt-2">📦 <span className="font-semibold">Status:</span> <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">{result.status}</span></p>
            )}
            {result.total && (
              <p className="text-gray-800 font-semibold mt-1">💰 Total: {result.total}</p>
            )}
            {result.items && result.items.length > 0 && (
              <div className="mt-3 border-t pt-3">
                <p className="font-semibold text-gray-700 mb-2">📦 Items:</p>
                <div className="space-y-2">
                  {result.items.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-white p-2 rounded shadow-sm">
                      {item.image && (
                        <img src={item.image} alt={item.product} className="w-20 h-20 object-cover rounded" />
                      )}
                      <div className="flex-1">
                        <p className="font-semibold text-gray-800">{item.product}</p>
                        <p className="text-sm text-gray-600">Quantity: {item.quantity} × {item.price} TND</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      }

      // Product display
      if (result.name && result.price) {
        return (
          <div className="mt-2 p-3 bg-white rounded-lg shadow-sm border border-gray-200">
            <p className="font-semibold text-luxury-gold text-lg">✨ {result.name}</p>
            <p className="text-gray-800 font-bold mt-1">{result.price} TND</p>
            {result.stock !== undefined && (
              <p className="text-gray-600 text-sm">📦 Stock: {result.stock}</p>
            )}
            {result.category?.name && (
              <p className="text-gray-600 text-sm">🏷️ {result.category.name}</p>
            )}
            {result.images && result.images.length > 0 && (
              <img 
                src={result.images[0].url || result.images[0]} 
                alt={result.name} 
                className="mt-2 w-full h-48 object-cover rounded"
              />
            )}
          </div>
        );
      }

      // Earnings/Analytics display
      if (result.totalEarnings || result.totalRevenue) {
        return (
          <div className="mt-2 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
            <p className="font-bold text-green-800 text-xl">💰 {result.totalEarnings || result.totalRevenue}</p>
            {result.year && <p className="text-gray-700 text-sm">For year {result.year}</p>}
            {result.period && <p className="text-gray-700 text-sm">Period: {result.period}</p>}
          </div>
        );
      }

      // Generic object - show nicely formatted
      return (
        <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm">
          <p className="font-semibold text-luxury-gold mb-2">✓ Result:</p>
          <div className="space-y-1">
            {Object.entries(result).map(([key, value]) => (
              <p key={key} className="text-gray-700">
                <span className="font-semibold text-gray-800">{key}:</span>{' '}
                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
              </p>
            ))}
          </div>
        </div>
      );
    }

    // Primitive values
    return (
      <div className="mt-2 p-3 bg-gray-50 rounded text-sm">
        <p className="text-gray-700 font-semibold">{String(result)}</p>
      </div>
    );
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 bg-luxury-gold text-white p-4 rounded-full shadow-lg hover:bg-opacity-90 transition-all z-50 group"
        aria-label="Open AI Assistant"
      >
        <HiOutlineSparkles className="w-6 h-6 animate-pulse" />
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-luxury-charcoal text-white px-3 py-1 rounded text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
          AI Admin Assistant
        </span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 w-96 h-[600px] bg-white rounded-lg shadow-2xl flex flex-col z-50 animate-slide-up">
      {/* Header */}
      <div className="bg-gradient-to-r from-luxury-charcoal to-gray-800 text-white p-4 rounded-t-lg flex items-center justify-between">
        <div className="flex items-center gap-2">
          <HiOutlineSparkles className="w-5 h-5 text-luxury-gold" />
          <div>
            <h3 className="font-semibold">AI Admin Assistant</h3>
            <p className="text-xs text-gray-300">Powered by Groq</p>
          </div>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="p-1 hover:bg-white/10 rounded transition-colors"
        >
          <HiX className="w-5 h-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-lg p-3 ${
                message.role === 'user'
                  ? 'bg-luxury-gold text-white'
                  : message.isError
                  ? 'bg-red-100 text-red-800'
                  : 'bg-white text-gray-800 shadow'
              }`}
            >
              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              
              {/* Render AI-generated UI if available */}
              {message.generatedUI ? (
                <div 
                  className="mt-3"
                  dangerouslySetInnerHTML={{ __html: message.generatedUI }}
                />
              ) : message.result ? (
                formatResult(message.result)
              ) : null}
              
              {message.executed && (
                <p className="text-xs mt-2 opacity-75">
                  ✓ Action executed successfully
                </p>
              )}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white rounded-lg p-3 shadow">
              <div className="flex gap-2">
                <div className="w-2 h-2 bg-luxury-gold rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-luxury-gold rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-luxury-gold rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={sendMessage} className="p-4 bg-white border-t rounded-b-lg">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Ask me anything..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-luxury-gold text-sm"
            disabled={isLoading}
          />
          <button
            type="submit"
            disabled={isLoading || !inputMessage.trim()}
            className="bg-luxury-gold text-white p-2 rounded-lg hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <HiOutlinePaperAirplane className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Try: "Create a product", "Show pending orders", "Check inventory"
        </p>
      </form>
    </div>
  );
};

export default AdminAIChatbot;
